import * as Dialog from "@radix-ui/react-dialog";
import { useRef, useState } from "react";
import { ArrowRight, Check, X } from "lucide-react";
import { Button } from "@/design-system/primitives";
import { useCart } from "@/features/marketplace/stores/cart";
import { Icon } from "@/lib/Icon";
import type { LabPanel, LabPurchaseType, LabInterval } from "@/types/labs";
import { LabPurchaseOptions, labIntervalLabel } from "./LabPurchaseOptions";
import { labDisplayName, labMoney } from "./labPresentation";

export const LAB_PRODUCT_IMAGE =
  "/images/tasso/system-collection-device-transparent.png";
const scene =
  "bg-sunken bg-[url('/icons/system_scene_background.jpg')] bg-cover bg-center";

export function LabProductCard({
  panel,
  previewing,
  onOpen,
  onAdd,
}: {
  panel: LabPanel;
  previewing: boolean;
  onOpen: () => void;
  onAdd: () => void;
}) {
  const inCart = useCart((s) =>
    s.items.some((item) => item.id === panel.product_id),
  );
  return (
    <article className="flex flex-col min-w-0">
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Explore ${labDisplayName(panel.name)}`}
        className={`relative w-full pb-[128%] sm:pb-[110%] overflow-hidden text-left focus-visible:outline-accent ${scene}`}
      >
        <img
          src={LAB_PRODUCT_IMAGE}
          alt="System at-home collection device"
          width={720}
          height={960}
          className="absolute top-[8%] left-[24%] w-[52%] h-[65%] object-contain"
        />
        <span className="absolute left-8 top-8 sm:left-10 sm:top-10 bg-black/50 backdrop-blur-md px-10 py-6 text-page text-14">
          {labMoney(panel.subscription_price_cents, panel.currency)}{" "}
          <span className="text-10">/ test</span>
        </span>
        <span className="hidden sm:block absolute right-8 top-8 sm:right-10 sm:top-10 bg-white/90 px-8 py-6 type-label-xs">
          {panel.test_count} measurements
        </span>
        <div className="absolute inset-x-0 bottom-0 p-8 sm:p-10">
          <div className="bg-black/50 backdrop-blur-md text-page px-10 sm:px-15 py-12 flex flex-col gap-6 min-h-95">
            <span className="type-label-xs text-white/75">
              At-home blood test
            </span>
            <h3 className="font-mono text-14 sm:text-16 uppercase leading-120">
              {labDisplayName(panel.name)}
            </h3>
            <span className="text-12 text-white/80 flex items-center justify-between gap-8">
              Explore biomarkers <ArrowRight size={14} aria-hidden />
            </span>
          </div>
        </div>
      </button>
      <button
        type="button"
        onClick={onAdd}
        disabled={!panel.purchasable || previewing}
        className="w-full min-h-44 sm:h-50 flex items-center justify-between px-15 text-14 bg-ink text-page disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span>
          {!panel.purchasable
            ? "Currently unavailable"
            : previewing
              ? "Exit preview to purchase"
              : inCart
                ? "View cart"
                : "Add to cart"}
        </span>
        <span aria-hidden className="inline-flex w-8 h-10">
          <Icon name="Arrow" viewBox="0 0 10 12" />
        </span>
      </button>
    </article>
  );
}

export function LabProductPreview({
  panel,
  previewing,
  onClose,
  onAdd,
}: {
  panel: LabPanel;
  previewing: boolean;
  onClose: () => void;
  onAdd: (type: LabPurchaseType, interval: LabInterval) => void;
}) {
  const returnFocus = useRef(
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null,
  );
  const [selectedType, setPurchaseType] =
    useState<LabPurchaseType>("subscription");
  const [selectedInterval, setInterval] = useState<LabInterval>(1);
  const cartItem = useCart((s) =>
    s.items.find((item) => item.id === panel.product_id),
  );
  const inCart = Boolean(cartItem);
  const purchaseType = cartItem
    ? (cartItem.lab_purchase_type ?? "subscription")
    : selectedType;
  const interval = cartItem
    ? (cartItem.lab_interval_months ?? 1)
    : selectedInterval;
  const groups = panel.biomarker_groups?.length
    ? panel.biomarker_groups
    : [{ name: "Included measurements", tests: panel.tests }];
  const price = labMoney(
    cartItem?.price_cents ??
      (purchaseType === "subscription"
        ? panel.subscription_price_cents
        : panel.one_time_price_cents),
    panel.currency,
  );
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-dialog bg-black/20 backdrop-blur-[10px]" />
        <Dialog.Content
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            returnFocus.current?.focus();
          }}
          className="fixed z-dialog inset-x-10 top-1/2 -translate-y-1/2 mx-auto max-w-[92rem] max-h-[calc(100dvh-20px)] flex flex-col bg-page shadow-modal focus-visible:outline-none"
        >
          <header className="flex shrink-0 items-center justify-between border-b border-line-subtle bg-raised pl-20">
            <span className="type-label-sm">Explore your test</span>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close test preview"
                className="w-50 h-50 grid place-items-center bg-sunken text-ink"
              >
                <X size={20} />
              </button>
            </Dialog.Close>
          </header>
          <div className="overflow-y-auto min-h-0">
            <div className="grid md:grid-cols-[30%_1fr]">
              <div
                className={`relative min-h-170 md:min-h-280 flex items-center justify-center ${scene}`}
              >
                <img
                  src={LAB_PRODUCT_IMAGE}
                  alt="System at-home collection device"
                  className="h-140 md:h-250 object-contain p-10"
                />
                <span className="absolute left-10 bottom-10 bg-white/90 px-10 py-6 type-label-xs">
                  At-home collection
                </span>
              </div>
              <div className="p-20 md:p-30">
                <Dialog.Title className="font-mono uppercase text-24 md:text-28 leading-110">
                  {labDisplayName(panel.name)}
                </Dialog.Title>
                <Dialog.Description className="type-body-md text-ink-secondary leading-145 mt-12">
                  {panel.description}
                </Dialog.Description>
                <div className="mt-20 grid grid-cols-2 border-y border-line-subtle divide-x divide-line-subtle">
                  <div className="py-15 pr-15">
                    <p className="font-mono text-28">{panel.test_count}</p>
                    <p className="type-label-xs mt-5 text-ink-secondary">
                      Reported measurements
                    </p>
                  </div>
                  <div className="py-15 pl-15">
                    <p className="font-mono text-28">
                      {panel.collection_device_count ?? 1}
                    </p>
                    <p className="type-label-xs mt-5 text-ink-secondary">
                      Collection{" "}
                      {(panel.collection_device_count ?? 1) > 1
                        ? "devices"
                        : "device"}
                    </p>
                  </div>
                </div>
                <p className="type-body-sm leading-145 text-ink-secondary mt-12">
                  {panel.kit_description}.{" "}
                  {panel.collection_device_count === 2
                    ? "Collect with both devices and return both samples as directed in your kit."
                    : "Follow the collection and return instructions included in your kit."}
                </p>
              </div>
            </div>
            <section
              aria-label="Included biomarkers"
              className="px-20 md:px-30 py-25 border-t border-line-subtle"
            >
              <h3 className="font-mono uppercase text-18 mb-15">
                Inside your panel
              </h3>
              <div className="grid sm:grid-cols-2 gap-15">
                {groups.map((group) => (
                  <section
                    key={group.name}
                    className="border border-line-subtle bg-raised p-15"
                  >
                    <div className="flex justify-between gap-10 mb-12">
                      <h4 className="type-label-sm">{group.name}</h4>
                      <span className="type-label-xs text-ink-muted">
                        {group.tests.length}
                      </span>
                    </div>
                    <ul className="space-y-8">
                      {group.tests.map((test) => (
                        <li
                          key={test}
                          className="flex gap-8 type-body-md leading-140"
                        >
                          <Check
                            size={14}
                            className="shrink-0 mt-2 text-ink-muted"
                            aria-hidden
                          />
                          <span>{test}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
              <p className="type-body-sm text-ink-secondary mt-15 leading-145">
                Includes calculated values and ratios. Each panel has a
                different set of measurements. Results and your downloadable
                laboratory report appear in Labs.
              </p>
            </section>
            <div className="grid sm:grid-cols-2 gap-20 p-20 md:px-30 bg-raised border-t border-line-subtle">
              <LabPurchaseOptions
                purchaseType={purchaseType}
                interval={interval}
                disabled={!panel.purchasable || previewing || inCart}
                onChange={(type, months) => {
                  setPurchaseType(type);
                  setInterval(months);
                }}
              />
              <div>
                <p className="font-mono text-28">
                  {price}
                  <span className="text-14 text-ink-secondary"> / test</span>
                </p>
                <p className="type-body-sm text-ink-secondary leading-145 mt-8">
                  {purchaseType === "subscription"
                    ? `${price} today, then every ${labIntervalLabel(interval)}. One test per charge. Manage your subscription in Account.`
                    : "One test. One payment. No subscription."}
                </p>
                {panel.is_test_price && (
                  <p className="type-label-xs text-ink-muted mt-8">
                    Staging test price
                  </p>
                )}
              </div>
            </div>
          </div>
          <footer className="p-15 border-t border-line-subtle bg-page shrink-0">
            <Button
              className="w-full justify-between"
              disabled={!panel.purchasable || previewing}
              onClick={() => onAdd(purchaseType, interval)}
            >
              {!panel.purchasable
                ? "Currently unavailable"
                : previewing
                  ? "Exit preview to purchase"
                  : inCart
                    ? "View cart"
                    : `Add to cart · ${price}`}
              <ArrowRight size={16} aria-hidden />
            </Button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
